// Onboarding talks to `/onboarding-prospects`. Every route is authenticated
// and company-scoped server-side — there is no public path, unlike
// recruitment's job applications.
//
// The server already returns `prospectId` as a string (sanitiseProspect maps
// `_id`), so unlike the recruitment module nothing here needs to normalise an
// ObjectId. It also strips S3 keys to a `stored` boolean, so a prospect in
// this store never carries one.
const BASE = "/onboarding-prospects";

const server = () => process.env.VUE_APP_NODE_SERVER + BASE;

// Failures are returned rather than thrown, matching the recruitment module.
// The `error` code is carried through because the onboarding endpoints use it
// to distinguish refusals the UI must word differently — `stage_locked` and
// `stage_reserved` are both "no", but one is "not yet" and the other "never".
const failure = (httpStatus, response) => ({
  status: httpStatus,
  error: (response && response.error) || null,
  message: (response && response.message) || null,
  fields: (response && response.fields) || null,
  prospectId: (response && response.prospectId) || null,
});

export default {
  // GET Requests //

  // Gets one page of the company's prospects. The list endpoint returns
  // SUMMARY records — no jobPreferences, no notes — plus company-wide
  // `stageCounts` computed without the stage filter, so the board's column
  // headers stay correct while filtered.
  async getProspects(context, payload) {
    const token = context.rootGetters["authData/token"];
    const companyId = context.rootGetters["userData/companyId"];
    const options = payload || {};
    let response;
    let httpStatus;

    if (!companyId) {
      return { status: 400 };
    }

    const query = new URLSearchParams();

    if (options.page) {
      query.append("page", options.page);
    }
    if (options.pageSize) {
      query.append("pageSize", options.pageSize);
    }
    if (options.stage) {
      query.append("stage", options.stage);
    }

    const search = query.toString();

    context.commit("prospectsLoading", true);

    try {
      const res = await fetch(
        server() + "/retrieve-by-company/" + companyId + (search ? "?" + search : ""),
        {
          method: "GET",
          headers: { Authorization: "Bearer " + token },
        },
      );
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      context.commit("prospectsLoading", false);
      return { status: 500 };
    }

    context.commit("prospectsLoading", false);

    if (httpStatus !== 200) {
      return failure(httpStatus, response);
    }

    context.commit("prospects", response?.prospects || []);
    context.commit("prospectsPage", {
      page: response?.page,
      pageSize: response?.pageSize,
      total: response?.total,
      stageCounts: response?.stageCounts,
    });
    context.commit("prospectsTimestamp");

    return { status: 200 };
  },

  // Reset prospects timestamp
  prospectsTimestampReset(context) {
    context.commit("prospectsTimestampReset");
  },

  // Gets one prospect in full. Deliberately NOT committed to the store: the
  // full record carries a non-user's personal data, and this module is
  // excluded from localStorage persistence for that reason. The caller holds
  // it in component state for as long as the screen is open.
  async getProspect(context, payload) {
    const token = context.rootGetters["authData/token"];
    let response;
    let httpStatus;

    if (!payload || !payload.prospectId) {
      return { status: 400 };
    }

    try {
      const res = await fetch(server() + "/retrieve/" + payload.prospectId, {
        method: "GET",
        headers: { Authorization: "Bearer " + token },
      });
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 200) {
      return failure(httpStatus, response);
    }

    return { status: 200, prospect: response?.prospect || null };
  },

  // Gets a time-limited signed URL for a prospect's CV or one documentation
  // item. The URL expires in 300 seconds and is a credential, so it is
  // returned to the caller and never committed to the store.
  async getDocumentLink(context, payload) {
    const token = context.rootGetters["authData/token"];
    const options = payload || {};
    let response;
    let httpStatus;

    if (!options.prospectId || !options.documentType) {
      return { status: 400 };
    }

    const query = new URLSearchParams({ documentType: options.documentType });

    if (options.documentationId) {
      query.append("documentationId", options.documentationId);
    }

    try {
      const res = await fetch(
        server() +
          "/retrieve-document-link/" +
          options.prospectId +
          "?" +
          query.toString(),
        {
          method: "GET",
          headers: { Authorization: "Bearer " + token },
        },
      );
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 200) {
      return failure(httpStatus, response);
    }

    return {
      status: 200,
      url: response?.url || null,
      originalName: response?.originalName || null,
      expiresInSeconds: response?.expiresInSeconds || null,
    };
  },

  // POST / PATCH / DELETE Requests //

  // Creates a prospect, optionally with a resume in the same request.
  //
  // `companyId` goes in the multipart body rather than on the query string:
  // the onboarding routes run multer BEFORE checkOnboardingAccess precisely
  // so that `req.body` is populated when the access check reads it. (The
  // recruitment module has to use the query string because its toggle check
  // runs first.)
  //
  // The server returns `existingUser` when the email already belongs to a
  // platform user (US-12), and the prospect comes back LINKED to them. A
  // `userId` is never accepted from the client, so there is nothing to send.
  async createProspect(context, payload) {
    const token = context.rootGetters["authData/token"];
    const companyId = context.rootGetters["userData/companyId"];
    const options = payload || {};
    let response;
    let httpStatus;

    if (!companyId) {
      return { status: 400 };
    }

    const fd = new FormData();
    fd.append("companyId", companyId);
    fd.append("firstName", options.firstName);
    fd.append("lastName", options.lastName);
    fd.append("emailAddress", options.emailAddress);

    if (options.phoneNumber) {
      fd.append("phoneNumber", options.phoneNumber);
    }
    // The route is `uploadDocumentStrict.single("resume")` — a disallowed
    // type is rejected rather than silently dropped.
    if (options.file) {
      fd.append("resume", options.file, options.file.name);
    }

    try {
      // No Content-Type header: the browser sets it with the multipart
      // boundary, and setting it by hand breaks the upload.
      const res = await fetch(server() + "/create", {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
        body: fd,
      });
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 201) {
      return failure(httpStatus, response);
    }

    context.commit("prospectAdd", response?.prospect);

    return {
      status: 201,
      prospect: response?.prospect || null,
      existingUser: response?.existingUser || null,
      warnings: response?.warnings || [],
    };
  },

  // Updates a prospect: stage and sub-status, the collected fields, the three
  // compliance ticks, or an appended note.
  //
  // A read-through field on a LINKED prospect is refused with 409
  // `read_through_field` and the offending names in `fields` — those values
  // belong to the linked user's own profile, not to the prospect.
  async patchProspect(context, payload) {
    const token = context.rootGetters["authData/token"];
    const options = payload || {};
    let response;
    let httpStatus;

    if (!options.prospectId) {
      return { status: 400 };
    }

    try {
      const res = await fetch(server() + "/update", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        // The signed-in user's own id, the one the database stores and the
        // company user list is keyed by. The server would otherwise derive an
        // author from the Auth0 subject, which only equals this for identities
        // it created itself — a note from any other account resolved to nobody
        // and read as "Previous Employee".
        body: JSON.stringify({
          ...options,
          actingUserId: context.rootGetters["userData/userId"],
        }),
      });
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 200) {
      return failure(httpStatus, response);
    }

    context.commit("prospectUpdate", response?.prospect);

    return {
      status: 200,
      prospect: response?.prospect || null,
      existingUser: response?.existingUser || null,
      warnings: response?.warnings || [],
    };
  },

  // Removes a prospect and purges its uploaded files. Allowed at any stage,
  // including `onboarded` — removing the record does not touch the user or
  // their company membership.
  async deleteProspect(context, payload) {
    const token = context.rootGetters["authData/token"];
    const options = payload || {};
    let response;
    let httpStatus;

    if (!options.prospectId) {
      return { status: 400 };
    }

    try {
      const res = await fetch(server() + "/remove", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({ prospectId: options.prospectId }),
      });
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 200) {
      return failure(httpStatus, response);
    }

    context.commit("prospectRemove", { prospectId: options.prospectId });

    return { status: 200 };
  },

  // Replaces a prospect's CV. The server uploads, saves, then deletes the old
  // object — so a failure never leaves the record pointing at a file that is
  // gone.
  async uploadResume(context, payload) {
    const token = context.rootGetters["authData/token"];
    const options = payload || {};
    let response;
    let httpStatus;

    if (!options.prospectId || !options.file) {
      return { status: 400 };
    }

    const fd = new FormData();
    fd.append("prospectId", options.prospectId);
    fd.append("file", options.file, options.file.name);

    try {
      const res = await fetch(server() + "/upload-resume", {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
        body: fd,
      });
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 200) {
      return failure(httpStatus, response);
    }

    context.commit("prospectUpdate", response?.prospect);

    return { status: 200, prospect: response?.prospect || null };
  },

  // Attaches one documentation item. `documentationId` names which required
  // document this is — re-uploading the same id replaces that object.
  // The server accepts images and PDFs only here.
  // Deletes the prospect's resume (documentType "cv") or one documentation
  // entry (documentType "documentation" with a documentationId).
  async deleteDocument(context, payload) {
    const token = context.rootGetters["authData/token"];
    const options = payload || {};
    let response;
    let httpStatus;

    if (!options.prospectId || !options.documentType) {
      return { status: 400 };
    }

    try {
      const res = await fetch(server() + "/remove-document", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          prospectId: options.prospectId,
          documentType: options.documentType,
          documentationId: options.documentationId,
        }),
      });
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 200) {
      return failure(httpStatus, response);
    }

    context.commit("prospectUpdate", response?.prospect);

    return { status: 200, prospect: response?.prospect || null };
  },

  async uploadDocumentation(context, payload) {
    const token = context.rootGetters["authData/token"];
    const options = payload || {};
    let response;
    let httpStatus;

    if (!options.prospectId || !options.documentationId || !options.file) {
      return { status: 400 };
    }

    const fd = new FormData();
    fd.append("prospectId", options.prospectId);
    fd.append("documentationId", options.documentationId);
    fd.append("file", options.file, options.file.name);

    try {
      const res = await fetch(server() + "/upload-documentation", {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
        body: fd,
      });
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 200) {
      return failure(httpStatus, response);
    }

    context.commit("prospectUpdate", response?.prospect);

    return { status: 200, prospect: response?.prospect || null };
  },

  // Adds the prospect to the company as a user. Despite the name this is not
  // always account creation: where the prospect is already a platform user
  // they are simply added to the company, and no second identity is minted.
  //
  // Nothing gates this — it is available at any stage and regardless of the
  // compliance ticks. 201 means an account was created, 200 that an existing
  // user was added or the prospect was already onboarded.
  //
  // `companyId` is deliberately NOT sent: the server takes it from the stored
  // prospect, so a client cannot convert a prospect into another company.
  async addToCompany(context, payload) {
    const token = context.rootGetters["authData/token"];
    const options = payload || {};
    let response;
    let httpStatus;

    if (!options.prospectId) {
      return { status: 400 };
    }

    const body = { prospectId: options.prospectId };

    // Only the fields the endpoint accepts. Anything else is ignored
    // server-side; sending nothing further keeps that explicit here too.
    //
    // Each is forwarded only when the caller supplied it: the server reads an
    // absent field as "not supplied" and a present one as an instruction, and
    // uses the difference to decide whether to warn that the membership
    // details were ignored for an existing member.
    if (options.password) {
      body.password = options.password;
    }
    if (options.companyTitle) {
      body.companyTitle = options.companyTitle;
    }
    if (options.managerId) {
      body.managerId = options.managerId;
    }
    if (options.startDate) {
      body.startDate = options.startDate;
    }
    if (options.departments) {
      body.departments = options.departments;
    }
    if (options.locations) {
      body.locations = options.locations;
    }

    try {
      const res = await fetch(server() + "/add-to-company", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify(body),
      });
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 200 && httpStatus !== 201) {
      return failure(httpStatus, response);
    }

    context.commit("prospectUpdate", response?.prospect);

    return {
      status: httpStatus,
      prospect: response?.prospect || null,
      user: response?.user || null,
      warnings: response?.warnings || [],
    };
  },
};
